import { Button, VStack } from '@chakra-ui/react';
import {
  createDateFieldResolver,
  DatesFields,
  FloatingInputField,
  MultiDropdownField,
  TRenderValueFunc,
} from '@payler/ui-components';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import React, { FC, Suspense, useCallback, useEffect, useMemo} from 'react';
import {
  FormDataFasterPayments, FormDataFasterPayouts, TCreateFasterPayoutsReportPayload,
  TCreateReportPayloadV3,
  TReportFilterV3,
  TTransactionStatus
} from '../../../../../api/src';
import dayjs from 'dayjs';
import createLogger from 'debug';
import { useCurrencyOptions } from '../../hooks/use-currency-options';
import { intersection, isEqual } from 'lodash';
import { useGetContractsCurrencies } from '../../hooks/use-get-contracts-currencies';
import {
  NEW_REPORT_SBP_PAYMENTS_STATUSES, NEW_REPORT_SBP_PAYOUT_STATUSES
} from '../../const';
import {
  useCreateReportForGlobalSegmentMutation, useCreateFasterPaymentsReportMutation, useCreateFasterPayoutsReportMutation
} from '../../hooks/reports/misc';
import { yupResolver } from '@hookform/resolvers/yup/dist/yup';
import * as yup from 'yup';
import { useUIDateFormat } from '../../hooks/use-ui-dateFormat';
import {
  useMerchantContractsIds, useMerchantContractsSBPOptions,
  useOnlyGlobalSegmentFlag
} from '../../hooks/contracts';
import { useCheckMerchantCabinetFeatureFlag } from '../../config/ConfigProvider';
import { useApmPaymentsMethods } from '../../hooks/apm-payments/payment-methods';
import { useApmPaymentMethodsOptions } from '../../hooks/use-apm-payment-methods-options';
import { useFasterPayoutsFiltersFromUrl } from '../../hooks/faster-payouts/url';

const log = createLogger('NewReportForm');

type Props = {
  onSubmit?: VoidFunction;
};

type FormDataGlobalSegment = Omit<
  TCreateReportPayloadV3 & TReportFilterV3,
  'filter'
>;

type FormData = FormDataFasterPayments | FormDataGlobalSegment;

const APM_PAYOUT_STATUS_OPTIONS =  NEW_REPORT_SBP_PAYOUT_STATUSES.map(
  (o) => ({
    label: o,
    value: o,
  }),
);

const useDefaultsForGlobalSegment = () => {
  const contractIds = useMerchantContractsIds();
  const dateFormat = useUIDateFormat();
  return useMemo<FormDataGlobalSegment>(
    () => ({
      contractIds,
      name: '',
      startDate: dayjs().add(-1, 'month').format(dateFormat),
      endDate: dayjs().format(dateFormat),
      paymentMethods: [],
      currencyCodes: [],
      paymentTypes: [],
      statuses: [],
    }),
    [contractIds, dateFormat],
  );
};

const useCreateResolver = () => {
  const { t } = useTranslation();
  const dateFormat = useUIDateFormat();
  return useCallback(() => {
    return yupResolver(
      yup.object({
        startDate: createDateFieldResolver(t, {
          toFieldName: 'endDate',
          maxDate: dayjs(),
          dateFormat,
        }),
        endDate: createDateFieldResolver(t, {
          fromFieldName: 'startDate',
          maxDate: dayjs(),
          dateFormat,
        }),
      }),
    );
  }, [dateFormat, t]);
};

export const NewReportSBPPayoutForm = ({ onSubmit }: Props) => {
  return (
    <Suspense fallback={null}>
      <NewReportFormComp onSubmit={onSubmit} />
    </Suspense>
  );
};

const NewReportFormComp = ({ onSubmit: outerOnSubmit }: Props) => {
  const isEnabled = useCheckMerchantCabinetFeatureFlag(
    'reports.ru_global_splitting',
  );
  const isGlobal = useOnlyGlobalSegmentFlag();

  if (!isEnabled) {
    return <RuSegmentForm onSubmit={outerOnSubmit} />;
  }

  return isGlobal ? (
    <GlobalSegmentForm onSubmit={outerOnSubmit} />
  ) : (
    <RuSegmentForm onSubmit={outerOnSubmit} />
  );
};

const parseDate = (input?: string | null) => {
  if (!input) return undefined;

  const d = dayjs(input, "DD.MM.YYYY", true);
  if (d.isValid()) return input;

  const d2 = dayjs(input);
  return d2.isValid() ? input : undefined;
};

const RuSegmentForm: FC<Props> = ({ onSubmit }) => {
  const { t } = useTranslation(['reports', 'fields', 'common']);
  const methods = useForm<FormDataFasterPayouts>();

  const createFasterPayoutsReportMutation =
    useCreateFasterPayoutsReportMutation();



  const filters = useFasterPayoutsFiltersFromUrl();

  const contractId = Number(window.location.pathname.split('/')[1]);

  const handleSubmit = methods.handleSubmit(async (data) => {


  const parsedCreatedAtFrom = parseDate(filters.DateFrom);
  const createdAtTo = parseDate(filters.DateTo);
  const createdAtFrom = parsedCreatedAtFrom;


  const payload: TCreateFasterPayoutsReportPayload = {
  name: data.name,
  reportType: 'FasterPayouts',
  filter: {
    statuses: filters.Statuses as TTransactionStatus[],
    createdAtFrom,
    createdAtTo,
    amountFrom: filters.AmountFrom as number,
    amountTo: filters.AmountTo as number,
    rrn: filters.Rrn ?? undefined,
    contractIds: [contractId],
    currencyCodes: filters.Currency ? [filters.Currency as string] : undefined,
    OrderId: filters.OrderId ?? undefined,
    ...(filters.Source ? { fasterPayoutSource: filters.Source } : {}),
  },
};

    await createFasterPayoutsReportMutation.mutateAsync(payload);
    onSubmit?.();
  });

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit}>
        <VStack alignItems="stretch" spacing={3}>
          <WatchContracts />
          <FloatingInputField
            name="name"
            label={t('fields:reportName')}
            isRequired
            autoFocus
            autoComplete="off"
          />
          <Button
            type="submit"
          >
            {t('reports:create')}
          </Button>
        </VStack>
      </form>
    </FormProvider>
  );
};

const GlobalSegmentForm: FC<Props> = ({ onSubmit }) => {
  const { t } = useTranslation(['fields', 'reports']);
  const createReportForGlobalSegmentMutation =
    useCreateReportForGlobalSegmentMutation();
  const dateFormat = useUIDateFormat();
  const defaultValues = useDefaultsForGlobalSegment();
  const createResolver = useCreateResolver();
  const methods = useForm<FormDataGlobalSegment>({
    defaultValues,
    resolver: createResolver(),
  });

  const handleSubmit = methods.handleSubmit(
    async (data: FormDataGlobalSegment) => {
      const { name, ...rest } = data;
      log(createReportForGlobalSegmentMutation, data);
      await createReportForGlobalSegmentMutation.mutateAsync({
        name,
        filter: rest,
      });
      onSubmit?.();
    },
  );

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit}>
        <VStack alignItems="stretch" spacing={3}>
          <WatchContracts isApm />
          <FloatingInputField
            name="name"
            label={t('fields:reportName')}
            isRequired
            autoFocus
            autoComplete="off"
          />
          <Contracts />
          <DatesFields
            labelsKeys={['fields:dateFrom', 'fields:dateTo']}
            fieldNames={['startDate', 'endDate']}
            floating
            dateFormat={dateFormat}
            maxDate={dayjs()}
          />
          <ApmPayoutStatuses />
          <ApmPaymentMethods />
          <PaymentTypes />
          <Currencies isApm />
          <Button
            type="submit"
            isLoading={createReportForGlobalSegmentMutation.isPending}
          >
            {t('reports:create')}
          </Button>
        </VStack>
      </form>
    </FormProvider>
  );
};

const PaymentTypes = () => {
  const { t } = useTranslation(['fields']);
  return (
    <MultiDropdownField
      options={APM_PAYOUT_STATUS_OPTIONS}
      fieldName="paymentTypes"
      floating
      label={t('fields:paymentIndicator')}
    />
  );
};

const ApmPayoutStatuses = () => {
  const { t } = useTranslation(['fields']);
  return (
    <MultiDropdownField
      options={APM_PAYOUT_STATUS_OPTIONS}
      fieldName="sessionStatuses"
      floating
      label={t('fields:transactionStatus')}
    />
  );
};


const ApmPaymentMethods = () => {
  const { watch } = useFormContext();
  const contractIds = watch('contractIds');
  const { options, fieldName } = useApmPaymentMethodsOptions(contractIds);

  return (
    <MultiDropdownField
      options={options}
      fieldName="paymentMethods"
      floating
      label={fieldName}
    />
  );
};


const StatusPayments = () => {
  const { t } = useTranslation(['fields', 'common']);

  const options = NEW_REPORT_SBP_PAYMENTS_STATUSES.map(
    (o) => ({
      label: o,
      value: o,
    }),
  );

     return (
    <MultiDropdownField
      fieldName="statuses"
      options={options}
      preset="mc2"
      label={t('fields:paymentStatus')}
      selectAllLabel={t('common:selectAll')}
    />
  );
};



const Contracts = () => {
  const { t } = useTranslation(['fields', 'common']);
  const options = useMerchantContractsSBPOptions();
  const isSingleContract = options.length === 1;

  const renderSingleValueCallback: TRenderValueFunc = useCallback(
    ({ props }) => props.options[0]?.label || '',
    [],
  );

  return (
    <MultiDropdownField
      fieldName="contractIds"
      options={options}
      renderValue={
        // INFO: Нельзя позволять очистить единственное значение
        isSingleContract ? renderSingleValueCallback : undefined
      }
      label={t('fields:contract')}
      selectAllLabel={!isSingleContract ? t('common:allContracts') : undefined}
      preset={!isSingleContract ? 'mc3' : undefined}
      hideClearButton={isSingleContract}
    />
  );
};

const Currencies: FC<{ isApm?: boolean }> = ({ isApm }) => {
  const { watch } = useFormContext<FormData>();
  const contractIds = watch('contractIds');
  const { options, fieldName } = useCurrencyOptions({
    contractIds,
    isApm,
  });

  return (
    <MultiDropdownField
      options={options}
      fieldName="currencyCodes"
      floating
      label={fieldName}
      noEllipsis
    />
  );
};

const WatchContracts: FC<{ isApm?: boolean }> = ({ isApm }) => {
  const { watch, setValue } = useFormContext<FormData>();
  const [contractIds, currencyCodes, paymentMethods] = watch([
    'contractIds',
    'currencyCodes',
    'paymentMethods',
  ]);
  const getCurrencies = useGetContractsCurrencies(isApm);
  const availablePaymentsMethods = useApmPaymentsMethods(contractIds);

  useEffect(() => {
    const currencies = getCurrencies(contractIds);
    const resCurrencies = intersection(currencyCodes, currencies);

    if (!isEqual(currencyCodes?.sort(), resCurrencies?.sort())) {
      setValue('currencyCodes', resCurrencies);
    }
  }, [contractIds, currencyCodes, getCurrencies, setValue]);

  useEffect(() => {
    if (!isApm) {
      return;
    }
    const resPaymentsMethods = intersection(
      availablePaymentsMethods,
      paymentMethods,
    );
    if (!isEqual(paymentMethods?.sort(), resPaymentsMethods?.sort())) {
      setValue('paymentMethods', resPaymentsMethods);
    }
  }, [availablePaymentsMethods, isApm, paymentMethods, setValue]);

  return null;
};
